import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, X, Upload, Image } from "lucide-react";

export const AdminProperties = () => {
  const { token } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    reference: "",
    description: "",
    property_type: "vente",
    category: "maison",
    price: "",
    surface: "",
    rooms: "",
    bedrooms: "",
    bathrooms: "",
    address: "",
    city: "",
    postal_code: "",
    features: [],
    images: [],
    is_featured: false,
    is_exclusive: false,
    is_active: true
  });
  const [newFeature, setNewFeature] = useState("");
  const [newImageUrl, setNewImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  
  useEffect(() => {
    fetchProperties();
  }, [token]);
  
  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/admin/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des biens");
    } finally {
      setLoading(false);
    }
  };
  
  const resetForm = () => {
    setFormData({
      title: "",
      reference: "",
      description: "",
      property_type: "vente",
      category: "maison",
      price: "",
      surface: "",
      rooms: "",
      bedrooms: "",
      bathrooms: "",
      address: "",
      city: "",
      postal_code: "",
      features: [],
      images: [],
      is_featured: false,
      is_exclusive: false,
    is_exclusive: false,
      is_active: true
    });
    setEditingProperty(null);
  };
  
  const openEditDialog = (property) => {
    setEditingProperty(property);
    setFormData({
      title: property.title,
      reference: property.reference || "",
      description: property.description,
      property_type: property.property_type,
      category: property.category,
      price: property.price.toString(),
      surface: property.surface.toString(),
      rooms: property.rooms?.toString() || "",
      bedrooms: property.bedrooms?.toString() || "",
      bathrooms: property.bathrooms?.toString() || "",
      address: property.address,
      city: property.city,
      postal_code: property.postal_code,
      features: property.features || [],
      images: property.images || [],
      is_featured: property.is_featured,
      is_exclusive: property.is_exclusive || false,
      is_active: property.is_active
    });
    setDialogOpen(true);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        surface: parseFloat(formData.surface),
        rooms: formData.rooms ? parseInt(formData.rooms) : null,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null
      };
      
      if (editingProperty) {
        await axios.put(`${API}/properties/${editingProperty.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Bien modifié avec succès");
      } else {
        await axios.post(`${API}/properties`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Bien créé avec succès");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchProperties();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce bien ?")) return;
    
    try {
      await axios.delete(`${API}/properties/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Bien supprimé");
      fetchProperties();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };
  
  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature("");
    }
  };
  
  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };
  
  const addImage = () => {
    if (newImageUrl.trim()) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, newImageUrl.trim()]
      }));
      setNewImageUrl("");
    }
  };
  
  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    let added = 0;
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      try {
        const res = await axios.post(`${API}/upload/image`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFormData(prev => ({ ...prev, images: [...prev.images, res.data.url] }));
        added += 1;
      } catch (err) {
        toast.error(err.response?.data?.detail || `Échec de l'envoi de ${file.name}`);
      }
    }
    if (added) toast.success(`${added} photo${added > 1 ? "s" : ""} ajoutée${added > 1 ? "s" : ""}`);
    setUploading(false);
  };
  
  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price);
  };
  
  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-properties-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl text-[#0A0A0A]">Biens immobiliers</h1>
            <p className="text-gray-500 mt-1">{properties.length} bien(s) au total</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A]"
                data-testid="add-property-button"
              >
                <Plus size={18} className="mr-2" />
                Ajouter un bien
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">
                  {editingProperty ? "Modifier le bien" : "Ajouter un nouveau bien"}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-6 mt-4" data-testid="property-form">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="title">Titre *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Villa de prestige avec piscine"
                      required
                      data-testid="property-title-input"
                    />
                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="reference">Référence du bien</Label>
                    <Input
                      id="reference"
                      value={formData.reference}
                      onChange={(e) => setFormData(prev => ({ ...prev, reference: e.target.value }))}
                      placeholder="Ex : REF-2024-012"
                      data-testid="property-reference-input"
                    />
                    <p className="text-xs text-gray-500">Affichée sur l'annonce ; les candidats l'indiquent lors du dépôt de dossier.</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Type de transaction *</Label>
                    <Select
                      value={formData.property_type}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, property_type: value }))}
                    >
                      <SelectTrigger data-testid="property-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vente">Vente</SelectItem>
                        <SelectItem value="location">Location</SelectItem>
                        <SelectItem value="vendu">Vendu</SelectItem>
                        <SelectItem value="loue">Loué</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Catégorie *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger data-testid="property-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="maison">Maison</SelectItem>
                        <SelectItem value="appartement">Appartement</SelectItem>
                        <SelectItem value="terrain">Terrain</SelectItem>
                        <SelectItem value="garage">Garage</SelectItem>
                        <SelectItem value="parking">Parking</SelectItem>
                        <SelectItem value="commercial">Local commercial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="price">Prix (€) *</Label>
                    <Input
                      id="price"
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="250000"
                      required
                      data-testid="property-price-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="surface">Surface (m²) *</Label>
                    <Input
                      id="surface"
                      type="number"
                      value={formData.surface}
                      onChange={(e) => setFormData(prev => ({ ...prev, surface: e.target.value }))}
                      placeholder="120"
                      required
                      data-testid="property-surface-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="rooms">Pièces</Label>
                    <Input
                      id="rooms"
                      type="number"
                      value={formData.rooms}
                      onChange={(e) => setFormData(prev => ({ ...prev, rooms: e.target.value }))}
                      placeholder="5"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="bedrooms">Chambres</Label>
                    <Input
                      id="bedrooms"
                      type="number"
                      value={formData.bedrooms}
                      onChange={(e) => setFormData(prev => ({ ...prev, bedrooms: e.target.value }))}
                      placeholder="3"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="bathrooms">Salles de bain</Label>
                    <Input
                      id="bathrooms"
                      type="number"
                      value={formData.bathrooms}
                      onChange={(e) => setFormData(prev => ({ ...prev, bathrooms: e.target.value }))}
                      placeholder="2"
                    />
                  </div>
                </div>
                
                {/* Address */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="address">Adresse *</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="15 Chemin des Vignes"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="postal_code">Code postal *</Label>
                    <Input
                      id="postal_code"
                      value={formData.postal_code}
                      onChange={(e) => setFormData(prev => ({ ...prev, postal_code: e.target.value }))}
                      placeholder="33000"
                      required
                    />
                  </div>
                  
                  <div className="md:col-span-3 space-y-2">
                    <Label htmlFor="city">Ville *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="Bordeaux"
                      required
                    />
                  </div>
                </div>
                
                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    rows={5}
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Décrivez le bien en détail..."
                    required
                    data-testid="property-description-input"
                  />
                </div>
                
                {/* Features */}
                <div className="space-y-2">
                  <Label>Équipements & prestations</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      placeholder="Ex: Piscine, Garage, Jardin..."
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                    />
                    <Button type="button" variant="outline" onClick={addFeature}>
                      <Plus size={18} />
                    </Button>
                  </div>
                  {formData.features.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.features.map((feature, index) => (
                        <Badge key={index} variant="secondary" className="pr-1">
                          {feature}
                          <button
                            type="button"
                            onClick={() => removeFeature(index)}
                            className="ml-1 hover:text-red-500"
                          >
                            <X size={14} />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Images */}
                <div className="space-y-2">
                  <Label>Photos du bien</Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    multiple
                    className="hidden"
                    onChange={handleFilesSelected}
                    data-testid="property-image-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-dashed border-2 h-16 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="property-image-upload-button"
                  >
                    <Upload size={18} className="mr-2" />
                    {uploading ? "Envoi en cours..." : "Télécharger des photos (JPG, PNG, WEBP – 10 Mo max)"}
                  </Button>
                  <div className="flex gap-2">
                    <Input
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                      placeholder="ou coller une URL d'image : https://..."
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addImage())}
                      data-testid="property-image-url-input"
                    />
                    <Button type="button" variant="outline" onClick={addImage} data-testid="property-image-url-add-button">
                      <Image size={18} />
                    </Button>
                  </div>
                  {formData.images.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mt-2" data-testid="property-image-previews">
                      {formData.images.map((img, index) => (
                        <div key={index} className="relative group">
                          <img 
                            src={img} 
                            alt={`Image ${index + 1}`} 
                            className="w-full h-20 object-cover rounded"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`property-image-remove-${index}`}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Options */}
                <div className="flex flex-wrap items-center gap-8">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="is_featured"
                      checked={formData.is_featured}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))}
                      data-testid="property-featured-switch"
                    />
                    <Label htmlFor="is_featured">Coup de cœur</Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                      data-testid="property-active-switch"
                    />
                    <Label htmlFor="is_active">Actif (visible sur le site)</Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id="is_exclusive"
                      checked={formData.is_exclusive}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_exclusive: checked }))}
                      data-testid="property-exclusive-switch"
                    />
                    <Label htmlFor="is_exclusive">Exclusivité (visible sur le site)</Label>
                  </div>
                </div>
                
                {/* Submit */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setDialogOpen(false)}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit"
                    className="bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A]"
                    disabled={submitting}
                    data-testid="property-submit-button"
                  >
                    {submitting ? "Enregistrement..." : (editingProperty ? "Modifier" : "Créer")}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        
        {/* Properties Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">Image</TableHead>
                <TableHead>Titre</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Prix</TableHead>
                <TableHead>Ville</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : properties.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-gray-500">
                    Aucun bien pour le moment
                  </TableCell>
                </TableRow>
              ) : (
                properties.map((property) => (
                  <TableRow key={property.id} data-testid={`property-row-${property.id}`}>
                    <TableCell>
                      <img 
                        src={property.images?.[0] || '/placeholder-bien.svg'} 
                        alt={property.title}
                        className="w-16 h-12 object-cover rounded"
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{property.title}</p>
                        <p className="text-sm text-gray-500">{property.reference ? <span className="text-[#B8952B] font-medium">Réf. {property.reference} · </span> : null}{property.surface} m²</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        property.property_type === 'vente' ? 'badge-vente' : 
                        property.property_type === 'location' ? 'badge-location' :
                        property.property_type === 'vendu' ? 'bg-green-100 text-green-800 border-green-200' :
                        'bg-purple-100 text-purple-800 border-purple-200'
                      }>
                        {property.property_type === 'vente' ? 'Vente' : 
                         property.property_type === 'location' ? 'Location' :
                         property.property_type === 'vendu' ? 'Vendu' : 'Loué'}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatPrice(property.price)}
                      {property.property_type === 'location' && <span className="text-gray-500">/mois</span>}
                    </TableCell>
                    <TableCell>{property.city}</TableCell>
                    <TableCell>
                      <Badge variant={property.is_active ? "default" : "secondary"}>
                        {property.is_active ? "Actif" : "Inactif"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => window.open(`/bien/${property.id}`, '_blank')}
                        >
                          <Eye size={16} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openEditDialog(property)}
                          data-testid={`edit-property-${property.id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(property.id)}
                          data-testid={`delete-property-${property.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProperties;
